import Translation from 'components/Translation'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { MouseEvent, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { classNames } from 'utils/dom-helpers'
import { deleteApiRequest, postApiRequest, putApiRequest } from 'utils/fetch-helper'
import { GeneralFormValues } from './types'
import { useTenantContext } from 'context/TenantContext'
import EntryPointSelector, {
    DEFAULT_ENTRY_POINT_KEY,
    ENTRY_POINTS,
    IEntrypointOption,
} from './EntryPointSelector'
import GenericError from 'components/GenericError/GenericError'
import CustomSwitch from 'components/appointmentDrawer/CustomSwitch'
import { ReactElement } from 'react'
import DialogModal from '../Dialog'
import { getTranslation } from '../../../utils/language-helper'
import { SHOW_FREE_BOOKING, SHOW_MEMBERSHIPS } from './GeneralForm'
import {
    DEFAULT_COMMISSION,
    IPaymentPolicy,
    PAYMENT_POLICY_KEY,
    PAYMENT_POLICY_OPTIONS,
} from 'components/PaymentPolicy/PaymentPolicy'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import StepperInput from 'components/Stepper'
import { DISPLAY_FORM_TITLE_SUBTITLE, ENABLE_RECURRING_BOOKINGS, ENTRY_WITH_CATEGORY_KEY, handleDBSettingsSave } from 'types/settings'
import PreviewBookingPage from './PreviewBookingPage'

export const SHOW_STAFF_KEY = 'SHOW_STAFF'
export const SHOW_AUTHORIZATION_BUTTONS = 'SHOW_AUTHORIZATION_BUTTONS'
export const SHOW_ONLY_START_TIME = 'SHOW_ONLY_START_TIME'
export const SHOW_APPOINTMENT_LIST = 'SHOW_APPOINTMENT_LIST'
export const OFFERINGS_INTERVAL = 'offerings.interval'
export const CALENDAR_DISPLAY = 'CALENDAR_DISPLAY'

export const CALENDAR_DISPLAY_OPTIONS = [
    {
        id: 1,
        title: 'Date Strip',
        value: 'date_strip',
        translationKey: 'common.calendar_display_date_strip',
    },
    {
        id: 2,
        title: 'Calendar',
        value: 'calendar',
        translationKey: 'common.calendar_display_calendar',
    },
]

export const handleDBSettingsChange = (
    db_settings: any,
    key: string,
    value: string | undefined,
    tenantId: string
) => {
    const data = db_settings?.content?.find(
        (item: any) => item.name === key
    )

    let payload = {
        category: 'BUSINESS',
        id: !data ? '' : data?.id,
        name: key,
        value: value,
        tenantId,
    }

    let res
    if (!!value) {
        if (data?.id) {
            res = handlePutDbSetting(payload)
        } else {
            res = handlePostDbSetting(payload)
        }
    } else if (data?.id) {
        res = deleteApiRequest(`/v1/settings/${data.id}`)
    }

    return res
}

const handlePostDbSetting = async (payload: any) => {
    return await postApiRequest('/v1/settings', payload)
}

const handlePutDbSetting = async (payload: any) => {
    return await putApiRequest(`/v1/settings/${payload?.id}`, payload)
}

export default function BookingPageOptions({
    from_settings,
}: {
    from_settings?: boolean
}) {
    const { setGlobalError } = useAppErrorApiContext()
    const { setGlobalLoading, translations } = useAppContext()
    const { CustomBookingPageModal } = useAuth()
    const {
        tenant,
        db_settings,
        getDbSettings,
        getTenantSubdomains,
        display_form_title_subtitle: display_form_title_subtitle_cached,
        enable_recurring_bookings: enable_recurring_bookings_cached,
        show_services_include,
        intro_message,
        show_intro_border,
        primaryColor,
        locationBg
    } = useTenantContext()
    const { attributes } = CustomBookingPageModal
    const [is_dirty, setIsDirty] = useState(false)
    const [selectedEntryPoint, setSelectedEntryPoint] = useState(ENTRY_POINTS[0])
    const [enableEntryByCategory, setEnableEntryByCategory] = useState(false)
    const [display_services_include, toggleServicesInclude] = useState<boolean>(show_services_include.value as boolean)
    const [displayStaff, setDisplayStaff] = useState(false)
    const [displayFreeTrial, setDisplayFreeTrial] = useState(true)
    const [displayMemberships, setDisplayMemberships] = useState(true)
    const [displayAuthButtons, setDisplayAuthButtons] = useState(true)
    const [displayOnlyStartTime, setDisplayOnlyStartTime] = useState(false)
    const [displayAppointments, setDisplayAppointments] = useState(true)
    const [display_form_title_subtitle, toggleDisplayFormTitleSubtitle] = useState<boolean>(display_form_title_subtitle_cached)
    const [enable_recurring_bookings, toggleEnableRecurringBookings] = useState<boolean>(enable_recurring_bookings_cached)
    const [selectedPolicy, setSelectedPolicy] = useState(PAYMENT_POLICY_OPTIONS[0].value)
    const [defaultCommission, setDefaultCommission] = useState('0')
    const [commissionType, setCommissionType] = useState<'PERCENT' | 'EXACT'>('PERCENT')
    const [offeringInterval, setOfferingInterval] = useState<number>(30)
    const [selectedCalendarDisplay, setSelectedCalendarDisplay] = useState(CALENDAR_DISPLAY_OPTIONS[0].value)
    const [error, setError] = useState<IError>(DEFAULT_ERROR_STATE)
    const [prompt_message, toggleDialog] = useState<string | ReactElement>('')

    const {
        handleSubmit,
        formState: { errors },
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (selectedEntryPoint.value === 'LOCATIONS')
            setEnableEntryByCategory(false)
    }, [selectedEntryPoint.value])

    useEffect(() => {
        if (db_settings?.content) {
            handleDefaultEntryPoint()
            handleDefaultEntryByCategory()
            handleDefaultDisplayStaff()
            handleDefaultFreeTrialValue()
            handleDefaultDisplayMembershipValue()
            handleDefaultAuthDisplay()
            handleDefaultDisplayStartTime()
            handleDefaultDisplayAppointments()
            handleDefaultCalendarDisplay()
        }
    }, [db_settings]) //TODO: refactor this entire file so that settings are fetched and set in only 1 area

    useEffect(() => {
        toggleServicesInclude(show_services_include.value as boolean)
    }, [show_services_include])

    useEffect(() => {
        if (display_form_title_subtitle_cached != display_form_title_subtitle) {
            toggleDisplayFormTitleSubtitle(display_form_title_subtitle_cached)
        }
    }, [display_form_title_subtitle_cached])

    useEffect(() => {
        if (enable_recurring_bookings_cached != enable_recurring_bookings) {
            toggleEnableRecurringBookings(enable_recurring_bookings_cached)
        }
    }, [enable_recurring_bookings_cached])

    const handleDefaultAuthDisplay = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_AUTHORIZATION_BUTTONS
        )

        if (found) {
            setDisplayAuthButtons(found?.value == 'true')
        }
    }

    const handleDefaultDisplayAppointments = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_APPOINTMENT_LIST
        )

        if (found) {
            setDisplayAppointments(found?.value == 'true')
        }
    }

    const handleDefaultEntryByCategory = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === ENTRY_WITH_CATEGORY_KEY
        )
        if (found) {
            setEnableEntryByCategory(found?.value == 'true')
        }
    }

    const handleDefaultDisplayStaff = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_STAFF_KEY
        )
        if (found) {
            setDisplayStaff(found?.value == 'true')
        }
    }

    const handleDefaultEntryPoint = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === DEFAULT_ENTRY_POINT_KEY
        )

        if (found) {
            setSelectedEntryPoint(
                ENTRY_POINTS.find(
                    (item) => item.value === found?.value
                ) as IEntrypointOption
            )
        }
    }

    const handleDefaultFreeTrialValue = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_FREE_BOOKING
        )

        if (found) {
            setDisplayFreeTrial(found?.value == 'true')
        }
    }

    const handleDefaultDisplayMembershipValue = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_MEMBERSHIPS
        )

        if (found) {
            setDisplayMemberships(found?.value == 'true')
        }
    }

    const handleDefaultDisplayStartTime = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_ONLY_START_TIME
        )

        if (found) {
            setDisplayOnlyStartTime(found?.value == 'true')
        }
    }

    const handleDefaultCalendarDisplay = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === CALENDAR_DISPLAY
        )

        if (found) {
            setSelectedCalendarDisplay(found?.value)
        }
    }

    const hanldeEntryPoint = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            DEFAULT_ENTRY_POINT_KEY,
            selectedEntryPoint.value,
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleEntryByCategory = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            ENTRY_WITH_CATEGORY_KEY,
            JSON.stringify(enableEntryByCategory),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleStaffDbSettings = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_STAFF_KEY,
            JSON.stringify(displayStaff),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleFreeTrialDisplay = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_FREE_BOOKING,
            JSON.stringify(displayFreeTrial),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleMembershipsDisplay = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_MEMBERSHIPS,
            JSON.stringify(displayMemberships),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleAuthDisplay = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_AUTHORIZATION_BUTTONS,
            JSON.stringify(displayAuthButtons),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    useEffect(() => {
        handleDefaultPaymentPolicy()
        handleDefaultCommission()
        handleDefaultOfferingInterval()
    }, [db_settings])

    const handleDefaultPaymentPolicy = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === PAYMENT_POLICY_KEY
        )
        if (found) {
            setSelectedPolicy(found?.value)
        }
    }

    const handleDefaultCommission = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === DEFAULT_COMMISSION
        )
        if (found) {
            const splittedVal = found?.value?.split('-')
            setDefaultCommission(splittedVal[0])
            setCommissionType(splittedVal[1])
        }
    }

    const handleDefaultOfferingInterval = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === OFFERINGS_INTERVAL
        )
        if (found) {
            setOfferingInterval(parseInt(found.value))
        }
    }

    const handleDisplayStartTimeChange = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_ONLY_START_TIME,
            JSON.stringify(displayOnlyStartTime),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleDisplayAppointmentsChange = () => {
        let res: any = handleDBSettingsChange(
            db_settings,
            SHOW_APPOINTMENT_LIST,
            JSON.stringify(displayAppointments),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    function handleDisplayFormTitleSubtitle() {
        let res: any = handleDBSettingsChange(
            db_settings,
            DISPLAY_FORM_TITLE_SUBTITLE,
            JSON.stringify(display_form_title_subtitle),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    function handleEnableRecurringBookings() {
        let res: any = handleDBSettingsChange(
            db_settings,
            ENABLE_RECURRING_BOOKINGS,
            JSON.stringify(enable_recurring_bookings),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    function handleOfferingsInterval() {
        let res: any = handleDBSettingsChange(
            db_settings,
            OFFERINGS_INTERVAL,
            JSON.stringify(offeringInterval),
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    function handleCalendarDisplay() {
        let res: any = handleDBSettingsChange(
            db_settings,
            CALENDAR_DISPLAY,
            selectedCalendarDisplay,
            tenant?.id as string
        )

        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    const handleDbSettingsUpdate = () => { //TODO: refactor to update only fields that are dirty
        hanldeEntryPoint()
        handleEntryByCategory()
        handleStaffDbSettings()
        getTenantSubdomains()
        handleFreeTrialDisplay()
        handleMembershipsDisplay()
        handleAuthDisplay()
        handleDisplayStartTimeChange()
        handleDisplayAppointmentsChange()
        handleDisplayFormTitleSubtitle()
        handleEnableRecurringBookings()
        handleOfferingsInterval()
        handleCalendarDisplay()
        if (show_services_include.value != display_services_include) {
            handleDBSettingsSave({ ...show_services_include, value: display_services_include }, tenant?.id || "")
        }
    }

    const onSubmit: SubmitHandler<GeneralFormValues> = () => {
        try {
            setGlobalLoading(true)
            handleDbSettingsUpdate()
            toggleDialog(
                getTranslation(
                    'common.customize_booking_page_success',
                    translations
                )
            )
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    function handleCloseModal(e: MouseEvent<HTMLButtonElement>) {
        CustomBookingPageModal.setAttributes({})
        CustomBookingPageModal.close()
    }

    const paymentPolicyProps: IPaymentPolicy = {
        commissionType,
        defaultCommission,
        selectedPolicy,
        setCommissionType,
        setDefaultCommission,
        setSelectedPolicy,
        is_dirty,
        setIsDirty,
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)} className="flex w-full">
            {!!error?.message?.length && (
                <GenericError
                    error_message={error.message}
                    onCloseModal={() => setError(DEFAULT_ERROR_STATE)}
                    tag={error?.tag}
                />
            )}
            <div
                id="booking-general-form"
                className="flex flex-row w-full h-full overflow-y-auto"
            >
                <div className="flex flex-col w-1/2 p-5 h-full">
                    <EntryPointSelector
                        selectedEntryPoint={selectedEntryPoint}
                        setSelectedEntryPoint={setSelectedEntryPoint}
                        setIsDirty={setIsDirty}
                    />
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            disable={
                                selectedEntryPoint?.value === 'LOCATIONS'
                            }
                            label={
                                <Translation content_key="enable_category" />
                            }
                            labelStyles={{}}
                            value={enableEntryByCategory}
                            onChangeValue={(val) => {
                                setEnableEntryByCategory(val)
                                setIsDirty(true)
                                if (!val) {
                                    toggleServicesInclude(false)
                                }
                            }}
                        />
                        <span
                            title={translations['enable_category_tooltip']}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1 ml-2 mt-2 text-gray-400 cursor-help"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </span>
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            disable={
                                !enableEntryByCategory
                            }
                            label={
                                <Translation content_key="settings.show_services_include" />
                            }
                            value={display_services_include}
                            onChangeValue={(val) => {
                                toggleServicesInclude(val)
                                setIsDirty(true)
                            }}
                        />
                        <span
                            title={translations['settings.show_services_include_tooltip']}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1 ml-2 mt-2 text-gray-400 cursor-help"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </span>
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="display_staff" />
                            }
                            labelStyles={{}}
                            value={displayStaff}
                            onChangeValue={(val) => {
                                setDisplayStaff(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="booking_settings_hide_trial" />
                            }
                            labelStyles={{}}
                            value={displayFreeTrial}
                            onChangeValue={(val) => {
                                setDisplayFreeTrial(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="booking_settings_hide_membership" />
                            }
                            labelStyles={{}}
                            value={displayMemberships}
                            onChangeValue={(val) => {
                                setDisplayMemberships(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="booking_settings_hide_auth_buttons" />
                            }
                            labelStyles={{}}
                            value={displayAuthButtons}
                            onChangeValue={(val) => {
                                setDisplayAuthButtons(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="settings.display_only_start_time" />
                            }
                            labelStyles={{}}
                            value={displayOnlyStartTime}
                            onChangeValue={(val) => {
                                setDisplayOnlyStartTime(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="settings.display_appointments" />
                            }
                            labelStyles={{}}
                            value={displayAppointments}
                            onChangeValue={(val) => {
                                setDisplayAppointments(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="forms.display_title_subtitle" />
                            }
                            labelStyles={{}}
                            value={display_form_title_subtitle}
                            onChangeValue={(val) => {
                                toggleDisplayFormTitleSubtitle(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <CustomSwitch
                            label={
                                <Translation content_key="settings.enable_recurring_bookings" />
                            }
                            labelStyles={{}}
                            value={enable_recurring_bookings}
                            onChangeValue={(val) => {
                                toggleEnableRecurringBookings(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <div className='w-44 flex flex-col gap-2'>
                            <Translation render_as='span' content_key="settings.set_offerings_interval" />
                            <StepperInput
                                default_value={
                                    offeringInterval
                                }
                                min_value={1}
                                max_value={300}
                                helper_text={getTranslation(
                                    offeringInterval == 1 ? "common.minute" : "common.minutes",
                                    translations
                                )}
                                onChange={(v: number) => {
                                    setOfferingInterval(v)
                                    setIsDirty(true)
                                }}
                            />
                        </div>
                    </fieldset>
                    <fieldset className="flex items-center mt-3">
                        <div className='flex flex-col'>
                            <Translation render_as='span' content_key={'common.default_calendar_display'} />
                            <select
                                className="flex w-full text-black px-2 py-1.5 rounded-md mt-2 border-2 border-gray-200 truncate"
                                onChange={(e) => {
                                    setSelectedCalendarDisplay(e.target.value)
                                    setIsDirty(true)
                                }}
                                value={selectedCalendarDisplay}
                            >
                                {CALENDAR_DISPLAY_OPTIONS.map((item) => (
                                    <option key={item.id} value={item.value}>
                                        {getTranslation(item?.translationKey, translations)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </fieldset>
                    {/* <PaymentPolicy {...paymentPolicyProps} /> */}
                    <div className="flex justify-end pt-12">
                        <DialogModal
                            prompt_message={prompt_message}
                            onCloseModal={() => {
                                toggleDialog('')
                                setIsDirty(false)
                                delete attributes?.refetch
                                CustomBookingPageModal.close()
                                getDbSettings()
                            }}
                        />
                        <button
                            type="button"
                            className={classNames(
                                'border border-gray-300 rounded-lg py-1 inline-block mr-3 px-2',
                                from_settings ? 'hidden' : 'visible'
                            )}
                            onClick={(e) => {
                                handleCloseModal(e)
                            }}
                        >
                            <Translation
                                content_key="cancel"
                                translations={translations}
                            />
                        </button>
                        <button
                            type="submit"
                            disabled={!is_dirty}
                            className={classNames(
                                'group relative flex justify-center',
                                'py-1 px-2 border border-transparent',
                                'rounded-md text-white',
                                'focus:outline-none',
                                'bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                !is_dirty ? 'opacity-60' : ''
                            )}
                        >
                            <Translation
                                content_key="save"
                                translations={translations}
                            />
                        </button>
                    </div>
                </div>
                <div className='hidden lg:flex flex-col w-1/2 bg-[#F1F3F4] h-full p-5'
                    style={{
                        boxShadow: 'inset 2px 0px 2px rgba(0, 0, 0, 0.05)'
                    }}
                >
                    <PreviewBookingPage
                        show_intro_message={!!intro_message?.id}
                        intro_message={intro_message?.value as string}
                        show_intro_border={show_intro_border?.value as boolean}
                        entry_point={selectedEntryPoint}
                        allow_entry_by_category={enableEntryByCategory}
                        show_services_include={display_services_include}
                        primary_color={primaryColor}
                        image_url={locationBg}
                    />
                </div>
            </div>
        </form>

    )
}
